import { useCallback, useRef } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useAutoSave, AutoSaveStatus } from './useAutoSave';

/** Un champ de premier niveau du formulaire (les panneaux n'écrivent jamais un sous-chemin). */
export type FieldName<V extends FieldValues> = Extract<keyof V, string>;

/**
 * Sauvegarde automatique d'un FORMULAIRE react-hook-form (panneau latéral).
 *
 * `useAutoSave` porte la règle (2026-09-22) et l'indicateur ; ce hook y ajoute
 * ce qu'un formulaire à validation impose :
 *  - un champ ne part que s'il passe SA validation (`form.trigger`) — une
 *    adresse incomplète reste sous le champ avec son erreur, rien n'est écrit ;
 *  - un champ ne part que s'il a CHANGÉ depuis ce que le serveur porte, comparé
 *    sur le morceau de payload qu'il produit (`toPatch`), pas sur la référence ;
 *  - sur refus du serveur, les champs reviennent à leur dernière valeur acquise.
 *
 * Plusieurs champs peuvent partir ensemble quand le serveur les lit ensemble
 * (un type et sa valeur, une borne basse et sa borne haute) : `saveFields` prend
 * une liste, validée et envoyée d'un bloc.
 *
 * `enabled` vaut faux en création : sans ligne en base, écrire au blur
 * créerait un objet fantôme par panneau abandonné.
 */
interface Options<V extends FieldValues, P extends object> {
  enabled: boolean;
  save: (patch: P) => Promise<void>;
  /** Le morceau de payload porté par ces champs, calculé sur les valeurs données. */
  toPatch: (fields: FieldName<V>[], values: V) => P;
  onError?: (error: unknown) => void;
}

export interface FormAutoSave<V extends FieldValues> {
  status: AutoSaveStatus;
  retry: (() => void) | null;
  /** Valide puis écrit ces champs s'ils ont changé. */
  saveFields: (fields: FieldName<V> | FieldName<V>[]) => Promise<void>;
  /** Repart de ces valeurs (changement d'objet édité) : formulaire ET référence. */
  reset: (values: V) => void;
}

export function useFormAutoSave<V extends FieldValues, P extends object>(
  form: UseFormReturn<V>,
  options: Options<V, P>,
): FormAutoSave<V> {
  const auto = useAutoSave<P>(options.save, { onError: options.onError });
  const baseline = useRef<V>(form.getValues());
  const { enabled, toPatch } = options;

  const reset = useCallback((values: V) => {
    form.reset(values);
    baseline.current = values;
  }, [form]);

  const saveFields = useCallback(async (fields: FieldName<V> | FieldName<V>[]) => {
    if (!enabled) return;
    const list = Array.isArray(fields) ? fields : [fields];
    if (!(await form.trigger(list as unknown as Path<V>[]))) return;
    const values = form.getValues();
    const patch = toPatch(list, values);
    if (JSON.stringify(patch) === JSON.stringify(toPatch(list, baseline.current))) return;
    const previous = baseline.current;
    const next = { ...previous } as V;
    list.forEach((f) => { (next as Record<string, unknown>)[f] = values[f]; });
    baseline.current = next;
    await auto.commit(patch, () => {
      baseline.current = previous;
      list.forEach((f) => form.setValue(f as unknown as Path<V>, previous[f]));
    });
  }, [enabled, form, toPatch, auto]);

  return { status: auto.status, retry: auto.retry, saveFields, reset };
}
